import Head from "next/head";
import Link from "next/link";
import dynamic from "next/dynamic";
import { getRandom } from "../../utils/blogUtils";
import blogData from "../../blogs.json";
import Service2 from "../../components/services/Service2";
const Capabilities2 = dynamic(() => import("../../components/services/Capabilities2"), { ssr: false });
import SuccessStoryEmbed from "../../components/trust/SuccessStoryEmbed";
import ServiceFAQ from "../../components/services/ServiceFAQ";
import ServiceEngagementLinks from "../../components/services/ServiceEngagementLinks";
import { CustomerSection } from "../../components/ai-agent-demo/CustomerSection";
const Footer = dynamic(() => import("../../components/Footer"), { ssr: false });

enterpriseApplications.title = "Enterprise Application Modernisation & ERP Consulting | ITMTB";
enterpriseApplications.desc =
  "ITMTB modernises legacy enterprise applications and ERP systems — making them integration-ready for AI agents and scalable for growth. SAP, Microsoft Dynamics 365, Oracle, Salesforce.";
enterpriseApplications.header_img = "https://www.itmtb.com/services/capability/automate.webp";
enterpriseApplications.keywords =
  "enterprise application modernisation India, ERP consulting India, legacy system modernisation AI, SAP Dynamics 365 consulting India, agent-ready enterprise systems, custom enterprise software development India";

const faqItems = [
  {
    q: "How do I make my legacy ERP ready for AI agents?",
    a: "Making a legacy ERP agent-ready is primarily an integration problem, not a replacement problem. AI agents need to be able to query and write to your ERP in real time — which requires either a well-documented API layer or a purpose-built integration service. Most legacy ERPs (SAP, older Dynamics versions, Tally) don't expose clean APIs by default; some require middleware (MuleSoft, Azure Integration Services, custom API gateways) to make their data accessible to external systems. The practical steps: (1) audit what your ERP exposes today — which entities, which operations, with what latency; (2) build an API layer that translates your ERP's data model into a clean, versioned interface your agents can call; (3) add event streaming for real-time data (Kafka, Azure Event Hub, or AWS EventBridge) so agents can react to ERP state changes rather than polling. We scope this integration architecture during the discovery sprint and implement it as a defined, testable layer — so your AI investment isn't blocked by data access.",
  },
  {
    q: "Which ERP is right for an Indian mid-market enterprise — SAP, Microsoft Dynamics 365, or Oracle?",
    a: "Each has a different fit depending on company size, industry, and existing technology footprint. SAP Business One or SAP S/4HANA Cloud is the right call when you need deep manufacturing or supply chain modules, are planning international expansion requiring multi-currency/multi-entity consolidation, or are in an industry where SAP is the de facto standard (automotive, chemicals, pharma). Microsoft Dynamics 365 Business Central is the right call when your team already lives in Microsoft 365, you want a low-friction integration with Azure and Power Platform, or you're a services or D2C business without complex manufacturing requirements. Oracle NetSuite is the right call for growing mid-market businesses with multi-entity, multi-currency requirements — particularly strong for professional services, distribution, and software companies that need consolidated financial reporting across subsidiaries. Oracle ERP Cloud suits larger enterprises with complex procurement, finance, and project management needs. We scope platform selection during the discovery sprint and give you a direct recommendation based on your specific workload, not vendor partnerships.",
  },
  {
    q: "What does enterprise application modernisation actually involve technically?",
    a: "Modernisation can mean different things depending on the current state. For a monolith being broken into microservices: the process is domain decomposition (identifying bounded contexts), strangler-fig migration (wrapping existing functionality progressively, not big-bang rewrite), API gateway introduction, and data store separation. For a legacy Java EE or .NET Framework application being moved to modern runtimes: containerisation, framework upgrade, dependency audit, and cloud deployment. For a data model that wasn't designed for AI: schema normalisation, CDC (change data capture) pipeline introduction, and API layer over the cleaned data. The right approach depends on why the system needs to be modernised — cost, scalability, AI readiness, or integration complexity. We define the target state and migration path during the discovery sprint, with a risk register that tells you what could break and how we'd handle it.",
  },
  {
    q: "How long does legacy system modernisation take?",
    a: "This is highly dependent on the size and complexity of the existing system, and what 'done' means. A focused API-enablement project (adding a clean REST API over a legacy system without changing the core) takes 6–10 weeks. A full monolith-to-microservices decomposition for a mid-size system takes 6–12 months depending on team size and the number of bounded contexts. A framework migration (Python 2 to 3, .NET Framework to .NET 8, Java EE to Spring Boot) without business logic changes typically takes 4–8 weeks for a medium-sized codebase. The discovery sprint (2 weeks, fixed price) produces a precise estimate for your specific system — including a risk register for the migration and a recommendation on migration path (rewrite vs. refactor vs. wrap).",
  },
  {
    q: "What's the difference between ERP implementation and custom enterprise application development?",
    a: "An ERP implementation takes an existing platform (SAP, Microsoft Dynamics 365, Oracle) and configures it to fit your business processes — defining workflows, setting up master data, integrating with other systems, and training your team. The software already exists; the work is configuration, integration, and change management. Custom enterprise application development builds something that doesn't exist: a bespoke application for a specific business process where no off-the-shelf product fits well enough. Custom development is justified when: your process is genuinely unique and a standard ERP would require so much customisation that it becomes unmaintainable; you need a purpose-built tool that connects multiple systems in a way no packaged product supports; or you're building a competitive differentiator (a proprietary logistics platform, a custom risk engine) that you don't want to run on a vendor's infrastructure. For most operational workflows, a well-implemented ERP is faster and cheaper than custom software. We give you a direct recommendation in the discovery sprint.",
  },
  {
    q: "How does ITMTB price an engagement?",
    a: "Pricing follows the engagement model. Discovery sprints are fixed-price for a fixed timebox. Build engagements are scoped after discovery — fixed-scope, time-and-materials, or outcome-linked. Managed services are billed monthly with defined SLAs. Total cost depends on scope, integration complexity, QA depth, and post-launch support — we publish a transparent breakdown for buyers evaluating their options.",
    link: "/blog/custom-software-development-cost-india",
    linkLabel: "Read the cost guide",
  },
  {
    q: "What does an engagement with ITMTB look like?",
    a: "Every engagement starts with a fixed-scope, fixed-price discovery sprint — typically 2 weeks. You leave with a written roadmap, risk register, cost estimate, and timeline before committing to a build. We then offer four engagement models: fixed-scope builds, time-and-materials, outcome-linked retainers, and managed services post-launch. If discovery doesn't justify proceeding, you can stop there — no obligation.",
    link: "/itmtb-how-we-work",
    linkLabel: "See how we work",
  },
];
enterpriseApplications.faq = faqItems;

const jsonLd = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Service",
      "name": "Enterprise Application Modernisation & ERP Consulting",
      "provider": {
        "@type": "Organization",
        "name": "ITMTB",
        "url": "https://www.itmtb.com",
        "logo": "https://www.itmtb.com/logo.webp",
        "sameAs": [
          "https://www.linkedin.com/company/itmtb-technologies/",
          "https://x.com/ITMTBTech",
        ],
      },
      "areaServed": { "@type": "Country", "name": "India" },
      "serviceType": "Enterprise Application Modernisation",
      "description":
        "ITMTB modernises legacy enterprise applications and ERP systems — making them integration-ready for AI agents and scalable for growth. SAP, Microsoft Dynamics 365, Oracle, Salesforce.",
      "offers": {
        "@type": "Offer",
        "priceSpecification": {
          "@type": "PriceSpecification",
          "description": "Fixed-price discovery sprint, then custom proposal based on scope",
        },
      },
    },
  ],
};

const activities = [
  "ERP implementation",
  "Legacy modernisation",
  "API enablement",
  "Agent integration layer",
  "Custom development",
  "Framework upgrades",
  "Database migration",
  "Cloud deployment",
  "Performance optimisation",
];

const engagementPhases = [
  {
    period: "Discovery Sprint",
    items: [
      "Architecture and dependency review",
      "API exposure and integration capability audit",
      "Risk register and migration path recommendation",
      "Written cost estimate and timeline",
      "Rewrite vs. refactor vs. wrap recommendation",
    ],
  },
  {
    period: "API & Integration Layer",
    items: [
      "Versioned API layer over legacy data stores",
      "Event streaming for real-time agent access",
      "Agent integration interface specification",
      "Security and access control architecture",
    ],
  },
  {
    period: "Progressive Migration",
    items: [
      "Strangler-fig approach: domain-by-domain",
      "Legacy system runs through the transition",
      "Framework and runtime upgrades per domain",
      "CI/CD pipeline maintained throughout",
    ],
  },
];

const comparisonRows = [
  {
    label: "Risk",
    col1: "High — entire system replaced at once",
    col2: "Medium — infrastructure changes, logic unchanged",
    col3: "Low — legacy runs until each domain is replaced",
  },
  {
    label: "Business continuity",
    col1: "Disruption during cutover",
    col2: "Minimal operational disruption",
    col3: "Continuous — no single cutover event",
  },
  {
    label: "Timeline",
    col1: "Longest — full rebuild required",
    col2: "Shortest — infrastructure only",
    col3: "Moderate — phased by domain count",
  },
  {
    label: "AI readiness result",
    col1: "Depends on new architecture decisions",
    col2: "Poor — same integration constraints remain",
    col3: "High — API layer built into migration",
  },
  {
    label: "Cost profile",
    col1: "High upfront, lower long-term maintenance",
    col2: "Low upfront, unchanged maintenance overhead",
    col3: "Moderate upfront, reduced maintenance overhead",
  },
  {
    label: "Recommended for",
    col1: "Greenfield replacement only",
    col2: "Infrastructure-constrained migrations",
    col3: "Most enterprise modernisation engagements",
  },
];

const modernisationPhases = [
  {
    phase: "01",
    title: "Discovery Sprint",
    duration: "2 weeks · Fixed price",
    items: [
      "Architecture review and dependency mapping",
      "API exposure and integration capability audit",
      "Risk register for chosen migration approach",
      "Cost estimate and timeline before build commitment",
      "Written recommendation: rewrite vs. refactor vs. wrap",
    ],
  },
  {
    phase: "02",
    title: "API & Integration Architecture",
    duration: "Weeks 3–6",
    items: [
      "Versioned API layer design over legacy data stores",
      "Event streaming architecture (Kafka, Azure Event Hub, EventBridge)",
      "Agent integration interface specification",
      "Security and access control design",
    ],
  },
  {
    phase: "03",
    title: "Progressive Migration",
    duration: "Phased delivery",
    items: [
      "Strangler-fig approach: domain-by-domain migration",
      "Legacy system runs until each domain is replaced",
      "Framework and runtime upgrades (Python 3, .NET 8, Spring Boot)",
      "CI/CD pipeline maintained through migration",
    ],
  },
  {
    phase: "04",
    title: "Handover & Operations",
    duration: "Post-delivery",
    items: [
      "Architecture documentation and runbooks",
      "Knowledge transfer and team training",
      "4–8 week post-launch warranty period",
      "Optional transition to managed services",
    ],
  },
];

const subServices = [
  {
    img: null,
    key: "Agent-Ready Enterprise Integration",
    title: "Your AI agents are only as good as the enterprise systems they can reach — we build the integration layer.",
    text: "Most legacy ERPs, CRMs, and internal applications don't expose clean APIs by default. AI agents that can't reach live operational data can't act on it. We audit what your systems expose today, build versioned API layers over legacy data stores, and add event streaming so agents can react to system state changes in real time.\n\nSystems we've integrated: SAP, Microsoft Dynamics 365, Salesforce, Tally, proprietary databases, and legacy Java EE / .NET applications.",
  },
  {
    img: null,
    key: "ERP Implementation and Customisation",
    title: "The right ERP for your business, implemented correctly from the start.",
    text: "We implement and customise ERP systems for mid-market enterprises: SAP Business One and S/4HANA Cloud for complex manufacturing and supply chain; Microsoft Dynamics 365 Business Central for Microsoft-stack organisations; Oracle NetSuite for multi-entity, multi-currency businesses; Oracle ERP Cloud for large enterprise requirements.\n\nImplementation includes workflow configuration, master data setup, system integration, and handoff training — not just licence installation.",
  },
  {
    img: null,
    key: "Legacy System Modernisation",
    title: "Strangler-fig migrations, not risky big-bang rewrites.",
    text: "We modernise legacy applications using a progressive approach: wrap existing functionality with a clean API, migrate functionality domain by domain, and retire the legacy system incrementally. This reduces risk compared to full rewrites and keeps your business running through the transition.\n\nMigration paths we've executed: monolith to microservices, Python 2 to 3, Java EE to Spring Boot, .NET Framework to .NET 8, on-premises to cloud-native.",
  },
  {
    img: null,
    key: "Custom Enterprise Application Development",
    title: "When no off-the-shelf product fits your process well enough to be worth the customisation.",
    text: "Custom development is justified for processes that are genuinely unique: a proprietary logistics coordination platform, a custom risk engine, a bespoke workflow tool that connects systems in a way no packaged product supports. We scope whether custom development is warranted versus ERP configuration during the discovery sprint — and give you an honest recommendation.\n\nFor the cases where custom is the right call, we build production-grade systems with proper testing, monitoring, and documentation — not prototypes handed off without ops support.",
  },
  {
    img: null,
    key: "Application Performance and Reliability",
    title: "Software that degrades silently is worse than software that fails loudly.",
    text: "Performance problems in enterprise applications have two root causes: architectural decisions that don't scale, and incremental degradation as data volumes and usage patterns change. We diagnose both: database query profiling, API latency analysis, memory and CPU profiling under load, and cache effectiveness review.\n\nFor cloud-hosted applications, performance is also a cost issue — slow queries that run frequently are expensive. We profile, optimise, and add monitoring so you catch regressions before your users do.",
  },
];

export default function enterpriseApplications() {
  let insights = blogData.filter(item => !["case-study", "tech"].includes(item?.meta?.category));
  let caseStudies = blogData.filter(item => item?.meta?.category === "case-study");

  let filteredInsights = insights.filter(item => item?.meta?.refer?.includes("Enterprise Applications and Automation"));
  let filteredCaseStudies = caseStudies.filter(item => item?.meta?.refer?.includes("Enterprise Applications and Automation"));

  if (filteredInsights.length === 1) {
    insights = [...filteredInsights, ...getRandom(insights.filter(item => !filteredInsights.includes(item)), 1)];
  } else if (filteredInsights.length >= 2) {
    insights = getRandom(filteredInsights, 2);
  } else {
    insights = getRandom(insights, 2);
  }

  if (filteredCaseStudies.length === 1) {
    caseStudies = [...filteredCaseStudies, ...getRandom(caseStudies.filter(item => !filteredCaseStudies.includes(item)), 1)];
  } else if (filteredCaseStudies.length >= 2) {
    caseStudies = getRandom(filteredCaseStudies, 2);
  } else {
    caseStudies = getRandom(caseStudies, 2);
  }

  return (
    <div>
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
        />
      </Head>

      {/* Hero */}
      <Service2
        heading="Enterprise Application Modernisation & ERP Consulting"
        paragraph={`AI agents can only act on data they can reach. For most enterprises, that means ERP, CRM, and internal applications need to be integration-ready before AI deployment can begin.<ul style="margin-top:16px;padding-left:20px;list-style:disc;display:flex;flex-direction:column;gap:8px"><li>SAP, Microsoft Dynamics 365, Oracle — implemented and customised for enterprises</li><li>Strangler-fig migrations — not big-bang rewrites</li><li>API enablement and event streaming for agent-ready systems</li><li>Fixed-price discovery sprint before any build commitment</li></ul>`}
        primaryCta={null}
        heroSvg={
          <svg viewBox="0 0 440 400" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full max-w-md opacity-70" aria-hidden="true" role="presentation">
            {/* dot grid */}
            {[...Array(6)].map((_, row) =>
              [...Array(7)].map((_, col) => (
                <circle key={`${row}-${col}`} cx={40 + col * 60} cy={40 + row * 60} r="1.5" fill="#e2e8f0" />
              ))
            )}
            {/* enterprise module rectangles */}
            <rect x="155" y="20" width="130" height="38" rx="7" stroke="#94a3b8" strokeWidth="1.5"/>
            <rect x="15" y="155" width="100" height="38" rx="7" stroke="#94a3b8" strokeWidth="1.5"/>
            <rect x="325" y="155" width="100" height="38" rx="7" stroke="#94a3b8" strokeWidth="1.5"/>
            <rect x="55" y="305" width="110" height="38" rx="7" stroke="#94a3b8" strokeWidth="1.5"/>
            <rect x="275" y="305" width="110" height="38" rx="7" stroke="#94a3b8" strokeWidth="1.5"/>
            {/* dashed ring accent on top and bottom-right modules */}
            <rect x="148" y="13" width="144" height="52" rx="11" stroke="#94a3b8" strokeWidth="0.8" strokeOpacity="0.35" strokeDasharray="3 3"/>
            <rect x="268" y="298" width="124" height="52" rx="11" stroke="#94a3b8" strokeWidth="0.8" strokeOpacity="0.35" strokeDasharray="3 3"/>
            {/* lines from module centers to hub */}
            <path d="M 220 58 C 220 100 220 148 220 182" stroke="#cbd5e1" strokeWidth="1.5"/>
            <path d="M 115 174 C 148 182 172 188 202 196" stroke="#cbd5e1" strokeWidth="1.5"/>
            <path d="M 325 174 C 293 182 268 188 238 196" stroke="#cbd5e1" strokeWidth="1.5"/>
            <path d="M 110 324 C 148 304 176 260 208 218" stroke="#cbd5e1" strokeWidth="1.5"/>
            <path d="M 330 324 C 293 304 264 260 232 218" stroke="#cbd5e1" strokeWidth="1.5"/>
            {/* central hub — amber accent (integration layer) */}
            <circle cx="220" cy="200" r="26" stroke="#b45309" strokeWidth="1" strokeOpacity="0.3" strokeDasharray="5 3"/>
            <circle cx="220" cy="200" r="18" stroke="#b45309" strokeWidth="2"/>
            <circle cx="220" cy="200" r="7" fill="#b45309" fillOpacity="0.15" stroke="#b45309" strokeWidth="1.5"/>
            {/* inner dots on module rects — presence indicator */}
            <circle cx="220" cy="39" r="2.5" fill="#cbd5e1"/>
            <circle cx="65" cy="174" r="2.5" fill="#cbd5e1"/>
            <circle cx="375" cy="174" r="2.5" fill="#cbd5e1"/>
            <circle cx="110" cy="324" r="2.5" fill="#cbd5e1"/>
            <circle cx="330" cy="324" r="2.5" fill="#cbd5e1"/>
          </svg>
        }
      />

      {/* Anchor nav */}
      <nav className="bg-white border-b border-gray-100 px-6 md:px-[60px] py-2 overflow-x-auto">
        <div className="flex gap-6 text-sm font-medium whitespace-nowrap">
          {[
            { label: "Who it's for", href: "#who-its-for" },
            { label: "Modernisation approaches", href: "#comparison" },
            { label: "How we modernise", href: "#how-we-modernise" },
            { label: "Engagement model", href: "#engagement-model" },
            { label: "FAQ", href: "#faq" },
          ].map(({ label, href }) => (
            <a key={href} href={href} className="text-gray-500 hover:text-indigo-600 transition-colors py-1">
              {label}
            </a>
          ))}
        </div>
      </nav>

      <CustomerSection title="Trusted by" />

      {/* § Definitional opener */}
      <section className="px-8 md:px-[60px] py-12 bg-white border-t border-gray-100">
        <div className="max-w-4xl">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-5">
            What is enterprise application modernisation?
          </h2>
          <p className="text-gray-700 text-base md:text-lg leading-relaxed mb-4">
            Enterprise application modernisation is the process of updating legacy business systems — ERPs, CRMs, and internal platforms — so they integrate with modern tooling, expose clean data to AI agents, and scale without accumulating further technical debt.
          </p>
          <p className="text-gray-700 text-base md:text-lg leading-relaxed mb-4">
            The most common misconception is that modernisation means full replacement. In most cases it does not. The right approach is wrapping existing systems with a versioned API layer, migrating functionality domain by domain using a strangler-fig pattern, and retiring legacy components progressively — keeping business operations uninterrupted through the transition. Replacement also carries hidden lifecycle spend — the{" "}
            <Link href="/blog/custom-software-development-cost-india">
              <a className="text-indigo-600 font-medium hover:text-indigo-800 transition-colors">support, QA, and total-ownership economics</a>
            </Link>{" "}
            that rarely appear in the initial build estimate.
          </p>
          <p className="text-gray-700 text-base md:text-lg leading-relaxed">
            ITMTB has delivered enterprise application modernisation and ERP engagements for businesses across India, the United Kingdom, the United Arab Emirates, and Southeast Asia — covering SAP and Dynamics 365 implementations, legacy Java EE and .NET migrations, and integration layers that made decade-old systems queryable by AI agents within weeks of engagement start.
          </p>
        </div>
      </section>

      {/* § Who this is for */}
      <section id="who-its-for" className="scroll-mt-24 px-8 md:px-[60px] py-14 bg-gray-50 border-t border-gray-100">
        <div className="max-w-4xl">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-10">
            Who This Is For
          </h2>

          <div className="space-y-10">
            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Enterprises whose ERP blocks AI adoption</h3>
              <p className="text-gray-600 text-base leading-relaxed">
                Your AI or automation initiative stalls because your ERP or CRM doesn't expose usable APIs. Agents can't query inventory, order status, or customer records in real time — so they operate on stale exports or can't act at all. Modernising the integration layer unblocks the AI investment without replacing the core system.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Companies evaluating SAP, Microsoft Dynamics 365, or Oracle</h3>
              <p className="text-gray-600 text-base leading-relaxed">
                Businesses that need to select and implement an ERP for the first time, or replace a system that no longer fits their scale. The right platform depends on your industry, technology footprint, and operational complexity — SAP for manufacturing and supply chain, Microsoft Dynamics 365 for Microsoft-integrated organisations, Oracle NetSuite for multi-entity mid-market businesses, Oracle ERP Cloud for large enterprise requirements.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Organisations running business-critical legacy applications</h3>
              <p className="text-gray-600 text-base leading-relaxed">
                Java EE, .NET Framework, older Python codebases, or proprietary systems that are production-critical but no longer actively maintained and difficult to extend. The risk of leaving them untouched grows with each year — a framework migration or strangler-fig decomposition reduces operational risk without requiring a complete rewrite.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Businesses building custom enterprise tooling</h3>
              <p className="text-gray-600 text-base leading-relaxed">
                Organisations with processes genuinely too specific for any off-the-shelf ERP: a proprietary logistics coordination platform, a custom risk engine, a bespoke workflow connecting systems in a way no packaged product supports. Custom development is warranted when configuration would produce an unmaintainable product — and counterproductive when a standard ERP implementation would serve the same outcome.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Teams preparing for DPDP Act or RBI / SEBI data requirements</h3>
              <p className="text-gray-600 text-base leading-relaxed">
                DPDP Act 2023 requires consent management and data principal rights workflows that most legacy CRMs and ERPs were not built to support. RBI data residency requirements for payment system operators and NBFCs create additional constraints on where data is stored and how it flows between systems. Modernisation engagements include compliance-aware architecture where applicable — and where regulatory exposure is the main driver, we begin with a{" "}
                <Link href="/services/cybersecurity">
                  <a className="text-indigo-600 font-medium hover:text-indigo-800 transition-colors">cybersecurity risk assessment</a>
                </Link>
                .
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* § Why enterprise systems need modernisation */}
      <section className="px-8 md:px-[60px] py-14 bg-white">
        <div className="max-w-4xl">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">
            Why Enterprise Systems Need to Modernise Before AI Can Deliver
          </h2>
          <p className="text-gray-700 text-base md:text-lg leading-relaxed mb-4">
            Most AI initiatives stall not because of the AI — but because the enterprise systems underneath don't expose data in a form agents can consume. Legacy ERPs, CRMs, and internal applications were not designed with external API access or real-time event streaming in mind.
          </p>
          <p className="text-gray-700 text-base md:text-lg leading-relaxed mb-10">
            ITMTB modernises enterprise applications and ERP systems so the systems you've built over the last decade remain operational while new capabilities are layered on top — without the risk of a big-bang rewrite.
          </p>

          {/* 9-item activity grid */}
          <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-5">
            Typical enterprise application activities
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 mb-10">
            {activities.map(activity => (
              <div
                key={activity}
                className="flex items-center gap-3 bg-gray-50 border border-gray-100 rounded-lg px-4 py-3"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-indigo-400 flex-shrink-0" />
                <span className="text-sm text-gray-700">{activity}</span>
              </div>
            ))}
          </div>

          <p className="text-gray-700 text-base md:text-lg leading-relaxed">
            Engagements cover discovery, API layer design, progressive migration, and post-delivery operations — helping enterprises extend the life of existing systems while making them ready for the next decade of tooling.
          </p>
        </div>
      </section>

      {/* § Engagement methodology */}
      <section id="engage" className="px-8 md:px-[60px] py-14 bg-gray-50 border-t border-gray-100">
        <div className="max-w-5xl">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">Engagement Methodology</h2>
          <p className="text-gray-600 text-base md:text-lg mb-10">
            Every engagement follows a defined sequence — no build commitment before the discovery sprint is complete.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {engagementPhases.map(phase => (
              <div key={phase.period} className="bg-white border border-gray-200 rounded-xl p-6">
                <p className="text-xs font-semibold uppercase tracking-widest text-indigo-500 mb-5">
                  {phase.period}
                </p>
                <ul className="space-y-3">
                  {phase.items.map(item => (
                    <li key={item} className="flex items-start gap-2 text-sm text-gray-700">
                      <span className="mt-1.5 w-1 h-1 rounded-full bg-gray-400 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <Link href="/itmtb-how-we-work">
            <a className="inline-flex items-center text-sm font-semibold text-indigo-600 hover:text-indigo-800 transition-colors">
              See the full engagement methodology →
            </a>
          </Link>
        </div>
      </section>

      {/* Success stories */}
      <SuccessStoryEmbed
        referTag="Enterprise Applications and Automation"
        maxItems={3}
        heading="Selected work in enterprise applications"
      />

      <section className="px-8 md:px-[60px] py-8 bg-white border-t border-gray-100">
        <p className="max-w-4xl text-sm text-gray-500 leading-relaxed">
          A regulated example of this pattern is our{" "}
          <Link href="/blog/pharma-compliance-ai-sop-bmr-warning-letter-review">
            <a className="text-indigo-600 font-medium hover:text-indigo-800 transition-colors">
              pharma compliance AI platform
            </a>
          </Link>
          , where enterprise identity, document workflow, auditability, and AI-assisted review had to work as one governed application.
        </p>
      </section>

      {/* § AI-ready systems */}
      <section className="px-8 md:px-[60px] py-14 bg-white border-t border-gray-100">
        <div className="max-w-4xl">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">
            Building the Integration Layer That AI Agents Need
          </h2>
          <p className="text-gray-700 text-base md:text-lg leading-relaxed mb-4">
            AI agents require access to live operational data — inventory levels, order status, customer records, financial positions. If your ERP or CRM doesn't expose this through a clean, versioned API, agents either operate on stale data or can't act at all.
          </p>
          <p className="text-gray-700 text-base md:text-lg leading-relaxed mb-4">
            The integration layer we build is not a workaround. It is a versioned, testable API over your existing data stores — with event streaming added where agents need to react to real-time state changes rather than polling. This layer outlasts any specific AI tooling you adopt.
          </p>
          <p className="text-gray-700 text-base md:text-lg leading-relaxed">
            Systems we have built integration layers for include SAP Business One, Microsoft Dynamics 365, Salesforce, Tally, proprietary Java EE applications, and legacy .NET systems — all without replacing the underlying system before the business is ready.
          </p>
        </div>
      </section>

      {/* § Comparison table */}
      <section id="comparison" className="scroll-mt-24 px-8 md:px-[60px] py-14 bg-white border-t border-gray-100">
        <div className="max-w-6xl overflow-x-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
            Strangler-Fig vs Big-Bang vs Lift-and-Shift: Which ERP Modernisation Approach Fits Your Risk Profile?
          </h2>
          <p className="text-gray-600 text-base mb-8">
            The structural differences between a big-bang rewrite, a lift-and-shift, and a progressive strangler-fig migration — and why most enterprise modernisation engagements suit the strangler-fig approach.
          </p>
          <table className="w-full border-collapse text-sm min-w-[640px]">
            <thead>
              <tr className="border-b-2 border-gray-200">
                <th className="text-left py-3 pr-6 w-[20%]" />
                <th className="text-left py-3 pr-6 text-gray-900 font-semibold w-[26%]">
                  Big-Bang Rewrite
                </th>
                <th className="text-left py-3 pr-6 text-gray-900 font-semibold w-[27%]">
                  Lift-and-Shift
                </th>
                <th className="text-left py-4 pr-6 text-indigo-900 font-semibold w-[27%] bg-indigo-50 px-4">
                  Strangler-Fig Migration
                  <br />
                  <span className="font-normal text-xs text-indigo-400">ITMTB approach</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {comparisonRows.map((row, idx) => (
                <tr
                  key={row.label}
                  className={idx % 2 === 0 ? "bg-gray-50/40" : "bg-white"}
                >
                  <td className="py-4 pr-6 text-gray-500 font-medium text-xs uppercase tracking-wide align-top border-b border-gray-100">
                    {row.label}
                  </td>
                  <td className="py-4 pr-6 text-gray-600 leading-snug align-top border-b border-gray-100">
                    {row.col1}
                  </td>
                  <td className="py-4 pr-6 text-gray-600 leading-snug align-top border-b border-gray-100">
                    {row.col2}
                  </td>
                  <td className="py-4 pr-6 text-gray-800 leading-snug bg-indigo-50/60 px-4 align-top font-medium border-b border-indigo-100">
                    {row.col3}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* § Modernisation phases */}
      <section id="how-we-modernise" className="scroll-mt-24 px-8 md:px-[60px] py-14 bg-gray-50 border-t border-gray-100">
        <div className="max-w-5xl">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
            How We Modernise Enterprise Systems
          </h2>
          <p className="text-gray-600 text-base md:text-lg mb-10">
            A structured four-phase process — scoped before any build commitment is made.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {modernisationPhases.map(phase => (
              <div key={phase.phase} className="bg-white border border-gray-200 rounded-xl p-6">
                <div className="flex items-start gap-4 mb-5">
                  <span className="text-3xl font-bold text-indigo-200 leading-none select-none">
                    {phase.phase}
                  </span>
                  <div>
                    <h3 className="text-base font-bold text-gray-900">{phase.title}</h3>
                    <p className="text-xs text-indigo-500 font-medium mt-1">{phase.duration}</p>
                  </div>
                </div>
                <ul className="space-y-2.5">
                  {phase.items.map(item => (
                    <li key={item} className="flex items-start gap-2 text-sm text-gray-700">
                      <span className="mt-1.5 w-1 h-1 rounded-full bg-gray-400 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* § Engagement model */}
      <section id="engagement-model" className="scroll-mt-24 px-8 md:px-[60px] py-14 bg-white border-t border-gray-100">
        <div className="max-w-4xl">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">Engagement Model</h2>
          <p className="text-gray-700 text-base md:text-lg leading-relaxed mb-4">
            Every engagement starts with a fixed-scope, fixed-price discovery sprint — typically two weeks. You leave with a written roadmap, risk register, cost estimate, and migration path recommendation before committing to a build.
          </p>
          <p className="text-gray-700 text-base md:text-lg leading-relaxed mb-4">
            Build engagements are then scoped based on discovery findings and offered as fixed-scope, time-and-materials, or outcome-linked retainers — depending on the nature of the migration and your team's involvement. Post-launch, managed services engagements are available to maintain and evolve the systems we deliver.
          </p>
          <p className="text-gray-700 text-base md:text-lg leading-relaxed mb-10">
            If the discovery sprint doesn't justify proceeding, you can stop there — with a written report that is useful regardless of whether you engage further.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-6">
              <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-4">What&apos;s included</p>
              <ul className="space-y-2.5">
                {[
                  "Discovery sprint: architecture review, API audit, risk register, cost estimate",
                  "API layer design and development, tested and versioned",
                  "Event streaming setup where real-time agent access is required",
                  "Domain-by-domain migration with legacy system running throughout",
                  "Architecture documentation, runbooks, and team handover",
                  "4–8 week post-launch warranty period",
                ].map(item => (
                  <li key={item} className="flex items-start gap-2 text-sm text-gray-700">
                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-indigo-400 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-6">
              <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-4">What&apos;s not included by default</p>
              <ul className="space-y-2.5">
                {[
                  "Business process redesign — we implement your process, not redesign it",
                  "End-user training beyond technical handover",
                  "Cloud infrastructure provisioning (handled via our cloud services engagement)",
                  "Ongoing post-launch operations — this transitions to a managed services engagement",
                  "ERP software licences (SAP, Microsoft Dynamics 365, Oracle — purchased separately)",
                ].map(item => (
                  <li key={item} className="flex items-start gap-2 text-sm text-gray-700">
                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-gray-300 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Capabilities */}
      <Capabilities2
        dataList={subServices}
        bg="bg-[#402A6A]"
        insights={[...caseStudies, ...insights]}
        hideFooter
        hideCardSubtitle
      />

      {/* § Related capabilities */}
      <section className="px-8 md:px-[60px] py-14 bg-gray-50 border-t border-gray-100">
        <div className="max-w-5xl">
          <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-7">
            Related capabilities
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              {
                href: "/services/custom-software-development",
                label: "Custom Software Development",
                desc: "Our core engineering practice — greenfield platforms, customer-facing and operational systems, and AI-enabled products. Enterprise application modernisation sits beneath it.",
              },
              {
                href: "/services/managed-services",
                label: "Managed IT Services",
                desc: "Operations management and production support for the systems we build or take over — yearly engagements with weekly reporting.",
              },
              {
                href: "/services/cloud",
                label: "Cloud Infrastructure",
                desc: "Architecture, migration, and operations for the cloud layer your modernised applications run on.",
              },
              {
                href: "/agentic-ai",
                label: "AI Agent Deployment",
                desc: "Deploying AI agent systems that connect to the enterprise integration layers we build.",
              },
              {
                href: "/services/tech-stack-audit",
                label: "Tech Stack Audit",
                desc: "An independent review of an existing application, cloud, and data stack before you modernise or rebuild — risks, technical debt, and a prioritised roadmap.",
              },
            ].map(link => (
              <Link key={link.href} href={link.href}>
                <a className="block bg-white border border-gray-200 rounded-xl p-6 hover:border-indigo-400 hover:shadow-sm transition-all group">
                  <h3 className="text-base font-semibold text-gray-900 group-hover:text-indigo-600 transition-colors mb-2">
                    {link.label}
                  </h3>
                  <p className="text-sm text-gray-500 leading-relaxed">{link.desc}</p>
                  <span className="mt-4 inline-block text-sm font-semibold text-indigo-500 group-hover:text-indigo-700 transition-colors">
                    Learn more →
                  </span>
                </a>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <div id="faq" className="scroll-mt-24"><ServiceFAQ items={faqItems} /></div>

      <ServiceEngagementLinks />

      {/* Page-specific CTA */}
      <section className="px-8 md:px-[60px] py-16 bg-[#0d0822]">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-xs font-semibold uppercase tracking-widest text-indigo-400 mb-5">
            Start the conversation
          </p>
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-5">
            Start with a Discovery Sprint
          </h2>
          <p className="text-slate-300 text-base md:text-lg leading-relaxed mb-8">
            Two weeks, fixed price. We review your architecture, audit integration
            exposure, map dependencies, and deliver a written migration recommendation
            — before you commit to a build.
          </p>
          <Link href="/contact-us?utm_source=service-enterprise-applications&utm_content=bottom-cta">
            <a className="inline-block bg-[#f1aa4e] text-black font-semibold text-base px-8 py-3.5 rounded-md hover:bg-[#e09a3e] transition-colors">
              Request a discovery sprint →
            </a>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
